import { z } from 'zod'
import { docId, isoTimestamp } from './common.js'

/**
 * A failure a user met, recorded so somebody hears about it.
 *
 * Until this existed, the only monitoring was somebody mentioning it. A
 * colleague's receipt uploads were refused for a month before he thought to
 * send a screenshot — and the people most likely to hit a broken path are the
 * least likely to report it, because they assume they are holding it wrong.
 *
 * What this is **not**: a crash log, a debugging aid, or anything anyone reads
 * routinely. It answers one question — "is somebody quietly stuck?" — and
 * everything in it is in service of that.
 *
 * Deliberately narrow about what it stores:
 *
 * - **The error code, never the raw message.** Firebase messages carry storage
 *   paths and document ids; the code (`storage/unauthorized`) is the part that
 *   identifies the fault.
 * - **A context the writer chose**, not a stack trace: "uploading a receipt",
 *   "loading budget codes". Something an administrator can act on.
 * - **No file names, vendors, amounts or account codes.** A problem report is
 *   read by whoever administers access, who may have no business seeing
 *   another person's spending.
 */
export const problemReportSchema = z.object({
  id: docId.optional(),

  /** Who hit it. The uid, so it joins to nothing a reader shouldn't see. */
  userId: docId,
  /** Shown to whoever reads the list, since a uid names nobody. */
  email: z.string().min(1).max(320),

  /** `storage/unauthorized`, `permission-denied`, … or `unknown`. */
  code: z.string().min(1).max(100),
  /** What the person was doing: "uploading a receipt". */
  context: z.string().min(1).max(200),

  at: isoTimestamp,
})

export type ProblemReport = z.infer<typeof problemReportSchema>

/**
 * Codes worth telling an administrator about.
 *
 * Deliberately short. A report for every transient hiccup makes a list nobody
 * reads, and a list nobody reads is the situation this replaces. These are the
 * failures that do not fix themselves: a permission that is missing, a session
 * that has expired, an index that was never deployed, a quota that is spent.
 *
 * Offline is excluded on purpose — it is the normal condition of a phone in a
 * building, the app already says so on screen, and it resolves itself.
 */
const WORTH_REPORTING = new Set([
  'permission-denied',
  'storage/unauthorized',
  'functions/permission-denied',
  'unauthenticated',
  'storage/unauthenticated',
  'functions/unauthenticated',
  'failed-precondition',
  'resource-exhausted',
  'storage/quota-exceeded',
  'internal',
  'functions/internal',
  'data-loss',
  // Added 2026-11-03. An upload failed, the receipt was lost, and *nothing*
  // was recorded — the code was not one of the above, so the only account of
  // it was the user remembering to mention it, which is the situation this
  // list exists to end. A storage failure costs a capture whatever it is
  // called, and "unknown" is the name Firebase gives the ones worth seeing.
  'storage/unknown',
  'storage/retry-limit-exceeded',
  'storage/server-file-wrong-size',
])

export function isWorthReporting(code: string | undefined | null): boolean {
  return code != null && WORTH_REPORTING.has(code)
}
